import * as React from 'react';
import { TextEdit } from '../../../text-edit';
import { PageProps } from './page.types';
import { usePageController } from './page.controller';
import { I18n } from 'react-redux-i18n';
import { Flex } from '@common/components/Flex';
import { Title } from '../../../title';
import { Button } from '@common/components/Button';
import { BackBtn } from '../../../back-btn';
import { FieldSelector } from '@common/components/FieldSelector';
import { Field } from '../field';
import { Subtitle } from '../../../subtitle';
import { RadioGroup, RadioGroupValue } from '../../../radio-group';
import { MODULE_NAME } from '@modules/fhq-design-tool/constants/shared';
import { SkipLogic } from '@common/components/skip-logic';
import { FieldProps } from '../field/field.types';
import { Loading } from '@common/components/loading';
import { SkipLogicPredefinedControlType, SkipLogicProps } from '@common/components/skip-logic/skip-logic.types';
import { FieldSelectorGetFieldParams } from '@common/components/FieldSelector/field-selector.types';
import { NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES } from '@modules/fhq-design-tool/constants/page';
import { TextEditor } from '../../../text-editor';
import { TextBlock } from '../text-block';
import { QuestionnaireTextBlockLocalDto } from '@modules/fhq-design-tool/dto/text-block-create-dto';
import { FieldsWithTextBlocks } from '../../../fields-with-text-blocks';
import { FieldInList } from '../../../fields-with-text-blocks/fields-with-text-blocks.types';

import './page.styles.scss';

export const Page: React.FC<PageProps> = (props) => {
  const { cachedData, showConfirmMessage, onGoBack, getFields, getFolders, scrollParentToTop, errors } = props;

  const {
    editingTextBlock,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    loadingPage,
    familyPagesSkipLogicConditions,
    handleSkipLogicChange,
    handleSkipLogicConditionAdd,
    handleUseSkipLogicChange,
    handleFieldChange,
    goBackFromFieldEdit,
    editingField,
    handleFieldAdd,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    navigateOnFieldEdit,
    canShowHideField,
    handlePageNameChange,
    handlePageDescriptionChange,
    fieldHasError,
    handleFieldsOrTextBlocksChange,
    handleAddTextBlock,
    combinedItems
  } = usePageController(props);

  if (editingField) {
    return (
      <Field
        errors={errors?.fields ? errors?.fields[editingField.progenyId] : undefined}
        scrollParentToTop={scrollParentToTop}
        data={editingField}
        onGoBack={goBackFromFieldEdit}
        onChange={handleFieldChange as FieldProps['onChange']}
        showConfirmMessage={showConfirmMessage}
        getFieldsForSkipLogic={getFields}
        getFoldersForSkipLogic={getFolders}
      />
    );
  }

  if (editingTextBlock) {
    return (
      <TextBlock
        data={editingTextBlock}
        onGoBack={closeTextBlockEditing}
        getFieldsForSkipLogic={getFields}
        getFoldersForSkipLogic={getFolders}
        onChange={handleTextBlockChange}
        errors={errors?.texts?.[editingTextBlock.id]}
      />
    );
  }

  if (loadingPage || !cachedData) {
    return (
      <div className="FhqDesignToolQuestionnaireDesignerPage">
        <Loading size={20} margin="20px" />
      </div>
    );
  }

  return (
    <div className="FhqDesignToolQuestionnaireDesignerPage">
      <BackBtn className="FhqDesignToolQuestionnaireDesignerPage__back-btn" onClick={onGoBack} />
      <TextEdit 
        required
        errors={errors?.properties?.title}
        metadata={{
          description: 'page-name',
        }}
        value={cachedData.title}
        onChange={handlePageNameChange}
      />
      <TextEditor
        metadata={{
          description: 'page-description',
        }}
        label={I18n.t('Page Description')}
        value={cachedData.description ?? ''}
        onChange={handlePageDescriptionChange}
      />
      <Title text={I18n.t('Skip Logic')} />
      <Subtitle
        text={I18n.t('skipLogicDescriptionPage')}
      />
      <RadioGroup
        margin="12px 0 12px 0"
        value={JSON.stringify(cachedData.skipLogicEnabled ?? false)}
        options={[
          { id: "false", name: I18n.t("Show Always") },
          { id: "true", name: I18n.t("Show only if condition below is true"), },
        ]}
        onChange={handleUseSkipLogicChange as (v: RadioGroupValue) => void}
        metadata={{
          description: "page-use-skip-logic",
        }}
      />
      {cachedData.skipLogicEnabled && (
        <SkipLogic
          moduleName={MODULE_NAME}
          className="FhqDesignToolQuestionnaireDesignerPage__skip-logic"
          conditions={familyPagesSkipLogicConditions}
          onRemove={handleSkipLogicChange}
          onChange={handleSkipLogicChange}
          onAdd={handleSkipLogicConditionAdd as SkipLogicProps['onAdd']}
          getFolders={getFolders}
          getFields={getFields}
          controls={[
            { type: SkipLogicPredefinedControlType.moveUp, handler: handleSkipLogicChange },
            { type: SkipLogicPredefinedControlType.moveDown, handler: handleSkipLogicChange },
            { type: SkipLogicPredefinedControlType.add },
          ]} 
        />
      )}
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireDesignerPage__add-item-header">
        <Title text={I18n.t('Fields')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireDesignerPage__add-item-btn" onClick={openFieldChooser}>
          +
        </Button>
      </Flex>
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireDesignerFamilyTablePage__add-item-header">
        <Title text={I18n.t('Text')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireDesignerFamilyTablePage__add-item-btn" onClick={handleAddTextBlock}>
          +
        </Button>
      </Flex>
      <FieldsWithTextBlocks<FieldInList, QuestionnaireTextBlockLocalDto>
        combinedItems={combinedItems}
        onChange={handleFieldsOrTextBlocksChange}
        fieldProps={{
          hasError: fieldHasError,
          onEdit: navigateOnFieldEdit, 
          showDeleteConfirmMessage: showConfirmMessage,
          canShowHideField, 
        }}
        textBlocksProps={{
          onEdit: openTextBlockEditing,
          showDeleteConfirmMessage: showConfirmMessage,
          hasError: x => Boolean(errors?.texts?.[x.id]),
        }}
      />
      {fieldChooserOpened && (
        <FieldSelector
          withSearch
          getFolders={getFolders}
          getFields={({ searchKeyword, ...restParams }: FieldSelectorGetFieldParams) => 
            getFields({ searchQuery: searchKeyword, ...restParams, includeTables: true })
              .then((fields) => 
                fields.filter((x) => !NOT_ALLOWED_FOR_PAGE_ADD_FIELD_TYPES.includes(x.fieldTypeId))
              )
          }
          onChange={handleFieldAdd}
          close={closeFieldChooser}
          closeOnChange={false}
          multiple
        />
      )}
    </div>
  );
};